import { useCallback } from 'react';
import { Network, TerminalSquare, X } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { apiClient } from '@/api/client';
import { useTunnelStore } from '@/stores/tunnel-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useModalStore } from '@/stores/modal-store';
import { useToastStore } from '@/stores/toast-store';
import { getAllPanes } from '@/stores/workspace-store';
import { ActionPanel } from '@/components/shared/ActionPanel';
import { buildTunnelActions } from '@/lib/actions/tunnel-actions';
import { useDialogStore } from '@/stores/dialog-store';
import type { TunnelViewTab, TunnelConfig, ConnectionConfig, TunnelStatus, TerminalTab } from '@/types';

interface TunnelViewProps {
  tab: TunnelViewTab;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-dbgo-panel border rounded-lg p-3">
      <div className="text-2xs text-text-muted uppercase tracking-wider mb-1">{label}</div>
      <div className="text-sm text-text-primary font-mono">{value}</div>
    </div>
  );
}

const STATUS_LABELS: Record<TunnelStatus, string> = {
  stopped: 'Stopped',
  starting: 'Starting',
  running: 'Running',
  error: 'Error',
};

export function TunnelView({ tab }: TunnelViewProps) {
  const queryClient = useQueryClient();
  const activeTunnels = useTunnelStore((s) => s.activeTunnels);
  const setTunnelStatus = useTunnelStore((s) => s.setStatus);
  const removeTunnel = useTunnelStore((s) => s.removeTunnel);
  const closeTab = useWorkspaceStore((s) => s.closeTab);
  const openTab = useWorkspaceStore((s) => s.openTab);
  const switchTab = useWorkspaceStore((s) => s.switchTab);
  const root = useWorkspaceStore((s) => s.root);

  const active = activeTunnels[tab.tunnelId];
  const status: TunnelStatus = active?.status ?? 'stopped';

  // Fetch from the shared list endpoint (reuses the sidebar's cache) instead
  // of /api/tunnels/:id — which doesn't exist. Filtering client-side keeps
  // the API surface small and auto-invalidates when sidebar CRUD refreshes
  // the ['tunnels'] query.
  const { data: allTunnels = [] } = useQuery<TunnelConfig[]>({
    queryKey: ['tunnels'],
    queryFn: () => apiClient.get<TunnelConfig[]>('/api/tunnels'),
    staleTime: 30_000,
  });
  const tunnel = allTunnels.find((t) => t.id === tab.tunnelId);

  const { data: connections = [] } = useQuery<ConnectionConfig[]>({
    queryKey: ['connections'],
    queryFn: () => apiClient.get<ConnectionConfig[]>('/api/connections'),
  });

  const linkedConnections = connections.filter((c) => c.tunnelId === tab.tunnelId);

  // Find all active terminal tabs for this tunnel
  const allPanes = getAllPanes(root);
  const terminalTabs = allPanes.flatMap((p) =>
    p.tabs.filter((t): t is TerminalTab => t.type === 'terminal' && t.tunnelId === tab.tunnelId),
  );

  const handleNewTerminal = useCallback(() => {
    openTab({
      type: 'terminal',
      id: `terminal:${tab.tunnelId}:${Date.now()}`,
      tunnelId: tab.tunnelId,
      tunnelName: tab.tunnelName,
      title: `${tab.tunnelName} — Terminal`,
    });
  }, [tab.tunnelId, tab.tunnelName, openTab]);

  const startMutation = useMutation({
    mutationFn: () => apiClient.post<{ localPort: number }>(`/api/tunnels/${tab.tunnelId}/start`),
    onMutate: () => {
      setTunnelStatus(tab.tunnelId, 'starting');
    },
    onSuccess: (data) => {
      setTunnelStatus(tab.tunnelId, 'running', { localPort: data.localPort });
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Failed to start tunnel';
      setTunnelStatus(tab.tunnelId, 'error', { error: message });
    },
  });

  const stopMutation = useMutation({
    mutationFn: () => apiClient.post(`/api/tunnels/${tab.tunnelId}/stop`),
    onSuccess: () => {
      setTunnelStatus(tab.tunnelId, 'stopped');
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Failed to stop tunnel';
      setTunnelStatus(tab.tunnelId, 'error', { error: message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.delete(`/api/tunnels/${tab.tunnelId}`),
    onSuccess: () => {
      removeTunnel(tab.tunnelId);
      closeTab(tab.id);
      queryClient.invalidateQueries({ queryKey: ['tunnels'] });
    },
  });

  const openEditTunnel = useModalStore((s) => s.openEditTunnel);
  const addToast = useToastStore((s) => s.addToast);

  const authMethodLabel =
    tunnel?.authMethod === 'key'
      ? 'Private Key'
      : tunnel?.authMethod === 'password'
        ? 'Password'
        : 'SSH Agent';

  return (
    <div className="h-full overflow-y-auto bg-dbgo-workarea">
      <div className="max-w-3xl mx-auto p-6">
        {/* Header — colored when tunnel carries a colorTag, mirroring the
            Connection info tab treatment. */}
        <div
          className="flex items-center gap-3 mb-6 pl-3"
          style={{
            boxShadow: tunnel?.colorTag ? `inset 3px 0 0 0 ${tunnel.colorTag}` : undefined,
          }}
        >
          <div
            className="w-10 h-10 rounded-lg border flex items-center justify-center"
            style={{
              backgroundColor: tunnel?.colorTag ? `${tunnel.colorTag}22` : 'var(--dbgo-panel)',
              borderColor: tunnel?.colorTag ?? 'var(--border-default)',
            }}
          >
            <Network size={20} className="text-accent-teal" />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-semibold text-text-primary">{tab.tunnelName}</h2>
            <p className="text-xs text-text-secondary">SSH Tunnel</p>
          </div>
          {/* Status badge */}
          <div
            className={clsx(
              'px-2.5 py-1 rounded-full text-2xs font-medium uppercase tracking-wider',
              status === 'running' && 'bg-accent-green/15 text-accent-green',
              status === 'stopped' && 'bg-text-muted/15 text-text-muted',
              status === 'error' && 'bg-accent-red/15 text-accent-red',
              status === 'starting' && 'bg-accent-yellow/15 text-accent-yellow',
            )}
          >
            {STATUS_LABELS[status]}
          </div>
        </div>

        {/* Error message */}
        {active?.error && (
          <div className="mb-4 px-3 py-2 rounded-sm bg-accent-red/10 text-accent-red text-xs">
            {active.error}
          </div>
        )}

        {/* Stats grid */}
        {tunnel && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            <StatCard label="SSH Host" value={tunnel.host} />
            <StatCard label="SSH Port" value={tunnel.port} />
            <StatCard label="Username" value={tunnel.username} />
            <StatCard label="Auth Method" value={authMethodLabel} />
            <StatCard
              label="Local Port"
              value={
                status === 'running' && active?.localPort
                  ? String(active.localPort)
                  : tunnel.localPort
                    ? String(tunnel.localPort)
                    : 'Auto'
              }
            />
          </div>
        )}

        {/* Actions — mirrors the sidebar context menu for this tunnel. */}
        {tunnel && (
          <ActionPanel
            items={buildTunnelActions(
              tunnel,
              status,
              {
                onStart: () => startMutation.mutate(),
                onStop: () => stopMutation.mutate(),
                onOpenTerminal: handleNewTerminal,
                onCopySSHCommand: () => {
                  const sshCmd = `ssh -L ${tunnel.localPort ?? '?'}:localhost:${tunnel.port} ${tunnel.username}@${tunnel.host}`;
                  navigator.clipboard.writeText(sshCmd);
                  addToast('success', 'Copied SSH command to clipboard');
                },
                onEdit: () => openEditTunnel(tunnel),
                onShare: () => useModalStore.getState().openShareDialog({
                  resourceType: 'tunnel',
                  resourceId: tunnel.id,
                  resourceName: tunnel.name,
                }),
                // Type-to-confirm matches Drop Database / Drop Table. Callers
                // using the shared useDialogStore.showConfirm keep the destructive
                // UX consistent everywhere a delete is offered.
                onDelete: () => {
                  const linkedCount = linkedConnections.length;
                  const extra = linkedCount > 0
                    ? ` ${linkedCount} connection${linkedCount === 1 ? '' : 's'} currently reference this tunnel and will need to be re-pointed after deletion.`
                    : '';
                  useDialogStore.getState().showConfirm({
                    title: 'Delete Tunnel',
                    message: `This will permanently delete the SSH tunnel "${tunnel.name}". This action cannot be undone.${extra}`,
                    confirmText: tunnel.name,
                    danger: true,
                    onConfirm: async () => {
                      await new Promise<void>((resolve, reject) => {
                        deleteMutation.mutate(undefined, {
                          onSuccess: () => resolve(),
                          onError: (err) => reject(err instanceof Error ? err : new Error(String(err))),
                        });
                      });
                    },
                  });
                },
              },
              active?.localPort,
            )}
          />
        )}

        {/* Active terminal sessions */}
        <div className="mb-6">
          <h3 className="text-2xs font-semibold text-text-secondary uppercase tracking-widest mb-3">
            Active Terminals ({terminalTabs.length})
          </h3>
          {terminalTabs.length === 0 ? (
            <p className="text-xs text-text-muted">
              No active terminal sessions. Click &quot;New Terminal&quot; to start one.
            </p>
          ) : (
            <div className="space-y-1">
              {terminalTabs.map((t) => (
                <div
                  key={t.id}
                  className="flex items-center gap-2 px-3 py-2 bg-dbgo-panel border rounded-md group"
                >
                  <TerminalSquare size={14} className="text-accent-green flex-shrink-0" />
                  <button
                    onClick={() => switchTab(t.id)}
                    className="text-xs text-text-primary hover:text-accent-blue truncate flex-1 min-w-0 text-left"
                  >
                    {t.title}
                  </button>
                  {t.sessionId && (
                    <span className="text-[10px] font-mono text-text-muted flex-shrink-0">
                      {t.sessionId.slice(0, 8)}
                    </span>
                  )}
                  <button
                    onClick={() => closeTab(t.id)}
                    className="w-5 h-5 flex items-center justify-center rounded-sm text-text-muted hover:text-accent-red hover:bg-dbgo-tab-hover opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    title="Close terminal"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Linked connections */}
        <div>
          <h3 className="text-2xs font-semibold text-text-secondary uppercase tracking-widest mb-3">
            Connections Using This Tunnel ({linkedConnections.length})
          </h3>
          {linkedConnections.length === 0 ? (
            <p className="text-xs text-text-muted">
              No connections are using this tunnel yet.
            </p>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-dbgo-grid-header text-text-secondary">
                    <th className="text-left px-3 py-2 font-medium">Name</th>
                    <th className="text-left px-3 py-2 font-medium">Driver</th>
                    <th className="text-left px-3 py-2 font-medium">Host</th>
                    <th className="text-right px-3 py-2 font-medium">Port</th>
                  </tr>
                </thead>
                <tbody>
                  {linkedConnections.map((conn) => (
                    <tr
                      key={conn.id}
                      className="border-t border-subtle hover:bg-dbgo-grid-row-hover transition-colors"
                    >
                      <td className="px-3 py-1.5 font-mono text-text-primary">{conn.name}</td>
                      <td className="px-3 py-1.5 text-text-secondary">{conn.driverType}</td>
                      <td className="px-3 py-1.5 text-text-secondary font-mono">{conn.host}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-syn-number">{conn.port}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
